import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Calendar, Clock, Eye, Newspaper } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';

interface NewsItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  published_at: string;
  category: string;
  reading_minutes: number;
}

/**
 * News article card mirroring the MarketplaceListingCard `mobileCompact` footprint:
 * aspect-[4/3] hero, framed body with title (2 lines), meta row, fixed CTA bar.
 * Used 2-up on mobile and as a 280px tile on tablet/desktop so news + listings
 * read as one unified card system.
 */
const ArticleCard = ({ a }: { a: NewsItem }) => (
  <Link
    to={a.slug ? `/news/${a.slug}` : '#'}
    className={cn(
      'group relative flex h-full flex-col overflow-hidden rounded-2xl',
      'border border-border/60 bg-card',
      'shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_16px_-8px_rgba(0,0,0,0.08)]',
      'transition-all duration-300 hover:border-primary/30 hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)] active:scale-[0.99]',
    )}
  >
    {/* Hero image — same aspect as listing cards */}
    <div className="relative aspect-[4/3] flex-shrink-0 overflow-hidden bg-muted/50">
      {a.featured_image ? (
        <img
          src={a.featured_image}
          alt={a.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          onError={e => (e.currentTarget.style.display = 'none')}
        />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-primary/20 to-primary/5" />
      )}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/30 to-transparent" />

      {a.category && (
        <Badge className="absolute left-1.5 top-1.5 z-20 max-w-[calc(100%-3rem)] truncate border-0 px-1.5 py-[2px] text-[8.5px] font-semibold capitalize shadow-sm">
          {a.category}
        </Badge>
      )}
      {a.reading_minutes ? (
        <span className="absolute right-1.5 top-1.5 z-20 inline-flex items-center gap-0.5 rounded-md bg-black/55 px-1 py-[2px] text-[8.5px] font-medium text-white backdrop-blur-sm">
          <Clock className="h-2.5 w-2.5" />
          {a.reading_minutes}m
        </span>
      ) : null}
    </div>

    {/* Body — fixed vertical rhythm to match listing card */}
    <div className="flex min-h-0 flex-1 flex-col gap-2 p-2.5 sm:p-3">
      <h3 className="text-[12.5px] sm:text-[13.5px] font-semibold leading-[1.25] tracking-tight text-foreground line-clamp-2 min-h-[2.5em]">
        {a.title}
      </h3>

      {a.excerpt ? (
        <p className="text-[10.5px] leading-snug text-muted-foreground line-clamp-3 flex-1">
          {a.excerpt}
        </p>
      ) : (
        <div className="flex-1" />
      )}

      {a.published_at && (
        <div className="flex h-4 items-center gap-1 text-[10px] leading-none text-muted-foreground">
          <Calendar className="h-2.5 w-2.5 flex-shrink-0" />
          <span className="truncate">{format(new Date(a.published_at), 'MMM d, yyyy')}</span>
        </div>
      )}

      <div className="mt-auto pt-1">
        <span className="group/cta inline-flex w-full items-center justify-center gap-1 rounded-lg bg-primary py-2 text-[10.5px] font-semibold uppercase tracking-wider text-primary-foreground shadow-sm transition-all hover:bg-primary/90 active:scale-[0.98]">
          Read article
          <Eye className="h-3 w-3 transition-transform group-hover/cta:translate-x-0.5" />
        </span>
      </div>
    </div>
  </Link>
);

const MobileNewsCarousel = () => {
  const { data: articles, isLoading } = useQuery({
    queryKey: ['mobile-news-carousel'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select('id, title, slug, excerpt, featured_image, published_at, category, reading_minutes')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as NewsItem[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const [api, setApi] = useState<CarouselApi | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setActiveIdx(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  const Header = (
    <div className="mb-4 flex items-end justify-between md:hidden">
      <div className="flex items-center gap-2">
        <Newspaper className="h-4 w-4 text-primary" />
        <h2 className="text-base font-semibold">Insights & News</h2>
      </div>
      <Link to="/news" className="text-sm font-medium text-primary inline-flex items-center gap-1">
        View all <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );

  const DesktopHeader = (
    <div className="hidden md:block text-center mb-10 space-y-4">
      <div className="relative flex items-center justify-center w-full">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
        <div className="relative z-10 px-6 py-2.5 bg-background rounded-full border border-primary/30 shadow-sm backdrop-blur-sm inline-flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-primary" />
          <h2 className="text-lg font-semibold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Insights & News
          </h2>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Latest helicopter industry analysis, deals and reports
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <section className="py-6 md:py-12 bg-background">
        <div className="container mx-auto px-4 md:hidden">
          {Header}
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="aspect-[3/5] w-full rounded-2xl" />
            <Skeleton className="aspect-[3/5] w-full rounded-2xl" />
          </div>
        </div>
        <div className="hidden md:block w-full px-8 lg:px-16 max-w-[2000px] mx-auto">
          {DesktopHeader}
          <div className="flex gap-6 overflow-hidden justify-center">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[3/5] w-[280px] shrink-0 rounded-2xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!articles || articles.length === 0) return null;

  return (
    <section className="py-6 md:py-12 bg-background">
      {/* Mobile: 2-up carousel matching listing card footprint */}
      <div className="container mx-auto px-4 md:hidden">
        {Header}
        <Carousel opts={{ align: 'start', loop: false }} setApi={setApi} className="w-full">
          <CarouselContent className="-ml-2">
            {articles.map(a => (
              <CarouselItem key={a.id} className="h-auto pl-2 basis-1/2 shrink-0">
                <div className="h-full">
                  <ArticleCard a={a} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-1 h-9 w-9 bg-primary text-primary-foreground hover:bg-primary/90 border-0 shadow-md shadow-primary/30" />
          <CarouselNext className="right-1 h-9 w-9 bg-primary text-primary-foreground hover:bg-primary/90 border-0 shadow-md shadow-primary/30" />
        </Carousel>

      </div>

      {/* Tablet/Desktop: 280px tiles matching listing grid card width */}
      <div className="hidden md:block w-full px-8 lg:px-16 max-w-[2000px] mx-auto">
        {DesktopHeader}
        <Carousel
          opts={{ align: 'start', loop: false }}
          className="w-full"
        >
          <CarouselContent className="-ml-6">
            {articles.map(a => (
              <CarouselItem key={a.id} className="h-auto pl-6 basis-[280px] shrink-0">
                <div className="h-full">
                  <ArticleCard a={a} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-4 h-11 w-11 bg-primary text-primary-foreground hover:bg-primary/90 border-0 shadow-md shadow-primary/30" />
          <CarouselNext className="right-4 h-11 w-11 bg-primary text-primary-foreground hover:bg-primary/90 border-0 shadow-md shadow-primary/30" />
        </Carousel>

        <div className="text-center mt-8">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            View all articles <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MobileNewsCarousel;
